import React, { useState, useMemo } from 'react';
import type { Assignment, Technician } from '@shared/types';
import { TechnicianAssignmentView } from '../components/Assignment/TechnicianAssignmentView';
import { MobileAgenda } from '../components/Calendar/MobileAgenda';
import { Briefcase, Calendar as CalendarIcon, List as ListIcon, RefreshCw } from 'lucide-react';

interface TechnicianAssignmentsPageProps {
    myTechnician: Technician;
    allTechnicians: Technician[];
    onUpdateRemarque: (assignmentId: string, remarque: string) => void;
    onStartWorksite: (assignmentId: string) => void;
    onFinishWorksite: (assignmentId: string) => void;
}

type ViewMode = 'list' | 'agenda';

export const TechnicianAssignmentsPage: React.FC<TechnicianAssignmentsPageProps> = ({
    myTechnician,
    allTechnicians,
    onUpdateRemarque,
    onStartWorksite,
    onFinishWorksite,
}) => {
    const [viewMode, setViewMode] = useState<ViewMode>('agenda');
    const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Get all assignments for this technician, sorted by date
    const myAssignments = useMemo(() => {
        return [...myTechnician.assignments].sort((a, b) => {
            const dateA = a.startDate || '9999-12-31';
            const dateB = b.startDate || '9999-12-31';
            return dateA.localeCompare(dateB);
        });
    }, [myTechnician.assignments]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        // Force full page reload to fetch fresh data from server
        window.location.reload();
    };

    if (selectedAssignment) {
        return (
            <div>
                <button
                    onClick={() => setSelectedAssignment(null)}
                    className="glass-button text-gray-400 mb-4 flex items-center gap-2"
                >
                    ← Retour
                </button>
                <TechnicianAssignmentView
                    assignment={selectedAssignment}
                    technicians={allTechnicians}
                    onUpdateRemarque={onUpdateRemarque}
                    onStartWorksite={onStartWorksite}
                    onFinishWorksite={onFinishWorksite}
                    onClose={() => setSelectedAssignment(null)}
                />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Briefcase className="w-6 h-6 text-accent" />
                    <h2 className="text-2xl font-bold text-gray-100">Mes Chantiers</h2>
                </div>

                <div className="flex items-center gap-2">
                    {/* Refresh Button */}
                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className={`p-2 rounded-lg transition-all ${isRefreshing
                            ? 'bg-primary/10 text-primary/50 cursor-not-allowed'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-primary'
                            }`}
                        title="Rafraîchir les données"
                    >
                        <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
                    </button>

                    {/* View Switcher */}
                    <div className="flex bg-white/5 p-1 rounded-xl">
                        <button
                            onClick={() => setViewMode('agenda')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'agenda'
                                ? 'bg-primary text-white shadow-lg shadow-primary/20'
                                : 'text-gray-400 hover:bg-white/5'
                                }`}
                        >
                            <CalendarIcon size={16} />
                            Planning
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'list'
                                ? 'bg-primary text-white shadow-lg shadow-primary/20'
                                : 'text-gray-400 hover:bg-white/5'
                                }`}
                        >
                            <ListIcon size={16} />
                            Liste
                        </button>
                    </div>
                </div>
            </div> {/* This is the missing closing div */}

            {viewMode === 'agenda' ? (
                <div className="motion-safe:animate-fade-in">
                    <MobileAgenda
                        assignments={myAssignments}
                        onSelectAssignment={setSelectedAssignment}
                        onStartWorksite={onStartWorksite}
                        onFinishWorksite={onFinishWorksite}
                    />
                </div>
            ) : (
                <div className="grid gap-4">
                    {/* Reusing simple card render from before, or could extract to component */}
                    {myAssignments.length === 0 ? (
                        <div className="glass-panel p-8 text-center py-12">
                            <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-400">Aucun chantier assigné pour le moment</p>
                        </div>
                    ) : (
                        myAssignments.map(assignment => (
                            <button
                                key={assignment.id}
                                onClick={() => setSelectedAssignment(assignment)}
                                className="glass-panel p-4 text-left hover:bg-white/10 transition-all border w-full border-gray-500/20"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-gray-100 mb-1 truncate">{assignment.clientName}</h3>
                                        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                                            <span>
                                                📅 {new Date(assignment.startDate).toLocaleDateString('fr-FR')}
                                            </span>
                                            {assignment.city && (
                                                <span>📍 {assignment.city}</span>
                                            )}
                                        </div>
                                    </div>
                                    {assignment.status === 'in-progress' && (
                                        <div className="px-2 py-1 rounded-lg text-[10px] font-medium bg-blue-500/20 text-blue-400 border border-blue-500/50">
                                            En cours
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};
